import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../hooks/useTheme';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toastError } from '../../utils/toastHelper';

interface CustomLoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const CustomLoginForm: React.FC<CustomLoginFormProps> = ({
  onSuccess,
  onError
}) => {
  const { iniciarSesion } = useAuth();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      toastError('Por favor ingresa usuario y contraseña');
      return;
    }

    setLoading(true);

    try {
      await iniciarSesion({ username, password });
      onSuccess?.();

      // ✅ VERIFICAR SI HAY REDIRECCIÓN PENDIENTE DESDE EMAIL
      const pendingRedirect = sessionStorage.getItem('camping_redirect_after_login');
      const empresaIdFromEmail = sessionStorage.getItem('email_redirect_empresa_id');

      if (pendingRedirect) {
        // Limpiar redirección pendiente
        sessionStorage.removeItem('camping_redirect_after_login');

        // Redirigir a la ruta guardada (preserve query params si existen)
        window.location.href = pendingRedirect;
      } else if (empresaIdFromEmail) {
        // Si hay empresa desde email, dejar que AuthContext la maneje
        navigate('/admin', { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (error: any) {
      // ⭐ USAR WRAPPER COMO FUENTE ÚNICA
      // Si isSilentError = true, NO loguear (es error esperado, ej: 401 en login)
      // Si isSilentError = false, SÍ loguear (es error real que requiere debugging)
      if (!error?.isSilentError) {
        console.error('❌ Login failed:', error);
      }

      // Mostrar mensaje al usuario
      const errorMessage = error?.message || 'Error al iniciar sesión';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
          Usuario
        </label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
          placeholder="Ingresa tu usuario"
          disabled={loading}
          autoComplete="username"
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Contraseña
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
            placeholder="Ingresa tu contraseña"
            disabled={loading}
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          backgroundColor: colors.primary,
          color: '#FFFFFF'
        }}
        className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Iniciando sesión...
          </div>
        ) : (
          <div className="flex items-center">
            <LogIn size={16} className="mr-2" />
            Iniciar Sesión
          </div>
        )}
      </button>
    </form>
  );
};

export default CustomLoginForm;
